import React, { useEffect, useState } from "react";
import { CheckRounded, ChevronRightRounded, ExpandMoreRounded } from "@mui/icons-material";
import { Dropdown, Menu, MenuItem, Skeleton, Typography, type MenuButtonProps } from "@mui/joy";
import ProjectSwitcherButton from "./ProjectSwitcherButton";
import { useTenantScope } from "../contexts/TenantScopeContext";
import type { ProjectSwitcher } from "../hooks/useProjectSwitcher";
import { RowIntent } from "../contexts/admissionPrefetch";

const styles = {
    menu: { zIndex: 9999, minWidth: 280, maxHeight: "70vh", overflowY: "auto" },
    itemLabel: { flex: 1 },
    project: { pl: 4 },
    buttonSkeleton: { width: 180 },
    rowSkeleton: { width: 140 },
};

/** Company rows expand into their server-admitted projects; choosing a project starts a fresh Document. */
export default function TenantScopeSwitcher({
    color,
    legacy,
}: {
    color?: MenuButtonProps["color"];
    legacy?: ProjectSwitcher;
}) {
    const scope = useTenantScope();
    const [error, setError] = useState(false);
    const [companiesError, setCompaniesError] = useState(false);
    /** The company group the person opened (null: they closed it); undefined until they click
     *  one, while the selected company, or else the only active one, starts open. */
    const [expanded, setExpanded] = useState<string | null>();
    // A project row the pointer or focus rests on asks for its admission before the click.
    const [intent] = useState(() => new RowIntent());
    useEffect(() => () => intent.cancel(), [intent]);
    const isSelected = (project: (typeof scope.projects)[number]) =>
        scope.selectedProject?.project_id === project.project_id ||
        (scope.selectedProject?.slug === project.slug &&
            scope.companies.find((company) => company.company_id === project.company_id)?.slug ===
                scope.selectedCompany?.slug);
    if (scope.loading) return <Skeleton variant="text" sx={styles.buttonSkeleton} />;
    const mappedOrganizations = new Set(
        scope.projects.map((project) => project.provider_organization_id),
    );
    const legacyProjects =
        legacy?.organizations.filter(
            ({ organization }) => !mappedOrganizations.has(organization.organization_id),
        ) ?? [];
    if (
        scope.companies.length === 0 &&
        !scope.loadProjectChoices &&
        legacyProjects.length === 0 &&
        !legacy?.isLoading
    )
        return <Typography level="body-sm">No project access is available.</Typography>;
    const title = [scope.selectedCompany?.name, scope.selectedProject?.name]
        .filter(Boolean)
        .join(" / ");
    const activeCompanies = scope.companies.filter((company) => company.state === "active");
    const prefetch = scope.prefetchProject;
    const rest = (projectId: string) => {
        if (prefetch) intent.rest(projectId, () => prefetch(projectId));
    };
    const openCompanyId =
        expanded !== undefined
            ? expanded
            : (scope.selectedCompany?.company_id ??
              (activeCompanies.length === 1 ? activeCompanies[0].company_id : null));

    return (
        <Dropdown
            onOpenChange={(_event, open) => {
                if (!open) return;
                legacy?.loadOrganizations();
                void scope.loadProjectChoices?.().catch(() => setError(true));
            }}
        >
            <ProjectSwitcherButton
                color={color}
                label={
                    scope.selectedProject?.name ||
                    scope.selectedCompany?.name ||
                    legacy?.currentName ||
                    "Select project"
                }
                title={title}
                disabled={legacy?.switching || scope.switching}
                switching={legacy?.switching || scope.switching}
            />
            <Menu sx={styles.menu} placement="bottom-start">
                {activeCompanies.map((company) => {
                    const projects = scope.projects.filter(
                        (project) =>
                            project.company_id === company.company_id && project.state === "active",
                    );
                    const isExpanded = openCompanyId === company.company_id;
                    return (
                        <React.Fragment key={company.company_id}>
                            <MenuItem
                                onClick={(event) => {
                                    if (projects.length === 0)
                                        scope.selectCompany(company.company_id);
                                    else {
                                        event.preventDefault();
                                        (
                                            event as { defaultMuiPrevented?: boolean }
                                        ).defaultMuiPrevented = true;
                                        setExpanded(isExpanded ? null : company.company_id);
                                    }
                                }}
                                aria-expanded={isExpanded}
                            >
                                {isExpanded ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                                <Typography level="title-sm" sx={styles.itemLabel}>
                                    {company.name}
                                </Typography>
                            </MenuItem>
                            {isExpanded &&
                                projects.map((project) => (
                                    <MenuItem
                                        key={project.project_id}
                                        sx={styles.project}
                                        selected={isSelected(project)}
                                        component="a"
                                        href={scope.projectHref?.(project.project_id) ?? undefined}
                                        onMouseEnter={() => rest(project.project_id)}
                                        onFocus={() => rest(project.project_id)}
                                        onMouseLeave={() => intent.leave(project.project_id)}
                                        onBlur={() => intent.leave(project.project_id)}
                                        onClick={(event) => {
                                            const href = scope.projectHref?.(project.project_id);
                                            if (event.altKey && href) {
                                                event.preventDefault();
                                                window.open(href, "_blank", "noopener,noreferrer");
                                            } else if (
                                                !event.metaKey &&
                                                !event.ctrlKey &&
                                                !event.shiftKey &&
                                                event.button === 0
                                            ) {
                                                event.preventDefault();
                                                scope.selectProject(project.project_id);
                                            }
                                        }}
                                    >
                                        <Typography level="body-sm" sx={styles.itemLabel}>
                                            {project.name}
                                        </Typography>
                                        {isSelected(project) && <CheckRounded />}
                                    </MenuItem>
                                ))}
                        </React.Fragment>
                    );
                })}
                {(scope.hasMoreProjects || scope.loadProjectChoices) && (
                    <MenuItem
                        disabled={scope.projectsLoading}
                        onClick={(event) => {
                            event.preventDefault();
                            (event as { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
                            setError(false);
                            void scope.loadMoreProjects?.().catch(() => setError(true));
                        }}
                    >
                        {scope.projectsLoading ? (
                            <Skeleton variant="text" sx={styles.rowSkeleton} />
                        ) : error ? (
                            "Retry loading projects"
                        ) : (
                            "Load more projects"
                        )}
                    </MenuItem>
                )}
                {scope.hasMoreCompanies && (
                    <MenuItem
                        disabled={scope.companiesLoading}
                        onClick={(event) => {
                            event.preventDefault();
                            (event as { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
                            setCompaniesError(false);
                            void scope.loadMoreCompanies?.().catch(() => setCompaniesError(true));
                        }}
                    >
                        {scope.companiesLoading ? (
                            <Skeleton variant="text" sx={styles.rowSkeleton} />
                        ) : companiesError ? (
                            "Retry loading companies"
                        ) : (
                            "Load more companies"
                        )}
                    </MenuItem>
                )}
                {legacyProjects.map(({ organization }) => (
                    <MenuItem
                        key={organization.organization_id}
                        onClick={() => legacy?.switchTo(organization.organization_id)}
                    >
                        <Typography level="body-sm" sx={styles.itemLabel}>
                            {organization.organization_name || organization.organization_slug}
                        </Typography>
                    </MenuItem>
                ))}
                {legacy?.isLoading && (
                    <MenuItem disabled>
                        <Skeleton variant="text" sx={styles.rowSkeleton} />
                    </MenuItem>
                )}
            </Menu>
        </Dropdown>
    );
}
